import { ClassScheduleSettings } from '../services/sectionLayoutService';
import { addDays, mondayOf, parseISODate, toISODate } from './date';
import {
  ANNUAL_WEEKDAY_KEYS,
  AnnualClassEntry,
  buildAnnualCellKey,
  buildAnnualClassEntries,
  buildAnnualPeriodLabels,
  deriveAnnualEffectiveCell,
  isAnnualShortDayTailMemo,
  isDedicatedColor,
  annualSlotIsFixedProgram,
} from './annualTimetable';
import { matchesAnnualProjectProgramCell, AnnualProjectProgram } from './annualProjectProgramsStorage';
import { AcademicTerm, AnnualTimetableCell, AnnualWeekdayKey, Grade, Track } from '../types/domain';
import {
  type GradeTrackTimetableContext,
  resolveTrackNameFromTimetableText,
} from './trackTimetableAlias';

export interface WeekInfo {
  weekIndex: number;
  mondayIso: string;
  datesByWeekday: string[];
}

export interface ProgramProgressRow {
  annualCellKey: string;
  /** 연간 일정 행 수를 넘는 «내용만» 행: 일정 열(순~시간표)은 비활성(회색) 처리 */
  isScheduleSlotOverflow?: boolean;
  gradeId: string;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  slotIndex: number;
  seq: number;
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  gradeLabel: string;
  semesterLabel: string;
  templateLabel: string;
  timetableText: string;
  timetableColorHex: string;
  timetableTextShrink: boolean;
  timetableSlotKind: AnnualTimetableCell['slot_kind'];
  programKindLabel: string;
  memo: string;
  isScheduleAvailable?: boolean;
  fixedContentId?: string;
  fixedOrderIndex?: number;
  fixedSourceAnnualCellKey?: string;
  fixedEventName?: string;
}

export interface ProgramProgressRowFilterInput {
  /** 해당 행의 연간 셀 키(필터에서 DB·스코프와 매칭할 때 사용) */
  annualCellKey: string;
  templateLabel: string;
  timetableText: string;
  timetableColorHex: string;
  isFixedProgram: boolean;
  isProjectProgram: boolean;
  isDedicatedProgram: boolean;
  programKindLabel: string;
}

function gradeNumberLabel(raw: string): string {
  const nums = (raw.match(/\d+/g) ?? []).map((v) => Number(v)).filter((n) => Number.isInteger(n));
  return nums.length > 0 ? String(nums[0]) : '';
}

function weekdayLabel(key: AnnualWeekdayKey): string {
  return ANNUAL_WEEKDAY_KEYS.find((w) => w.key === key)?.label ?? key;
}

function shortDateLabel(iso: string): string {
  const d = parseISODate(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}월${d.getDate()}일`;
}

export function buildWeeksFromRange(startsOn: string, endsOn: string): WeekInfo[] {
  if (!startsOn || !endsOn) return [];
  const endDate = parseISODate(endsOn);
  const endMonday = mondayOf(endsOn);
  const endMondayIso = toISODate(endMonday);
  let cur = mondayOf(startsOn);
  const list: WeekInfo[] = [];
  let weekIndex = 0;
  while (cur.getTime() <= endDate.getTime()) {
    const mondayIso = toISODate(cur);
    const datesByWeekday: string[] = [];
    for (let d = 0; d < 6; d += 1) {
      datesByWeekday.push(toISODate(addDays(cur, d)));
    }
    list.push({ weekIndex: ++weekIndex, mondayIso, datesByWeekday });
    cur = addDays(cur, 7);
  }
  const hasEndWeek = list.some((w) => w.mondayIso === endMondayIso);
  if (!hasEndWeek) {
    const datesByWeekday: string[] = [];
    for (let d = 0; d < 6; d += 1) {
      datesByWeekday.push(toISODate(addDays(endMonday, d)));
    }
    list.push({ weekIndex: ++weekIndex, mondayIso: endMondayIso, datesByWeekday });
  }
  return list;
}

/** 시간표명(또는 편제명) -> 표시용 편제명 */
export function buildTrackNameByTimetableTextByGrade(tracks: Track[]): Record<string, Record<string, string>> {
  const map: Record<string, Record<string, string>> = {};
  tracks.forEach((row) => {
    const gradeId = (row.grade_id ?? '').trim();
    const trackName = (row.name ?? '').trim();
    const timetableName = (row.description ?? '').trim();
    if (!gradeId || !trackName) return;
    if (!map[gradeId]) map[gradeId] = {};
    map[gradeId][trackName] = trackName;
    if (timetableName) map[gradeId][timetableName] = trackName;
  });
  return map;
}

export function resolveClassEntryByGradeId(
  schedule: ClassScheduleSettings | null,
  grades: Grade[],
  gradeId: string
): AnnualClassEntry | null {
  if (!gradeId) return null;
  const classEntries = buildAnnualClassEntries(schedule, grades);
  return classEntries.find((entry) => entry.gradeId === gradeId) ?? null;
}

export function buildProgramProgressRows(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  selectedEntry: AnnualClassEntry;
  gradeRawName: string;
  termDef: AcademicTerm;
  weeks: WeekInfo[];
  holidaySet: Set<string>;
  cellsByKey: Record<string, AnnualTimetableCell>;
  dedicatedColors: string[];
  baseExcludeColors: string[];
  regularColors: string[];
  annualProjectPrograms: AnnualProjectProgram[];
  trackNameByTimetableTextByGrade: Record<string, Record<string, string>>;
  gradeTrackTimetableContext?: GradeTrackTimetableContext | null;
  rowFilter?: (input: ProgramProgressRowFilterInput) => boolean;
}): ProgramProgressRow[] {
  const {
    workspaceId,
    academicYear,
    termNumber,
    selectedEntry,
    gradeRawName,
    termDef,
    weeks,
    holidaySet,
    cellsByKey,
    dedicatedColors,
    baseExcludeColors,
    regularColors,
    annualProjectPrograms,
    trackNameByTimetableTextByGrade,
    gradeTrackTimetableContext,
    rowFilter,
  } = params;
  const periodLabels = buildAnnualPeriodLabels(selectedEntry.sectionRows);
  const gradeNumber = gradeNumberLabel(gradeRawName);
  const out: ProgramProgressRow[] = [];
  let seq = 1;
  weeks.forEach((week) => {
    ANNUAL_WEEKDAY_KEYS.filter((w) => w.key !== 'sat').forEach(({ key }, weekdayIdx) => {
      const weekdayKey = key as AnnualWeekdayKey;
      const dateIso = week.datesByWeekday[weekdayIdx] ?? '';
      for (let slotIndex = 0; slotIndex < periodLabels.length; slotIndex += 1) {
        const stored = cellsByKey[buildAnnualCellKey(selectedEntry.gradeId, week.mondayIso, weekdayKey, slotIndex)];
        const effective = deriveAnnualEffectiveCell({
          workspaceId,
          academicYear,
          termNumber,
          gradeId: selectedEntry.gradeId,
          mondayIso: week.mondayIso,
          weekdayKey,
          slotIndex,
          dateIso,
          sectionRows: selectedEntry.sectionRows,
          slotLimitByDay: selectedEntry.slotLimitByDay,
          startsOn: termDef.starts_on,
          endsOn: termDef.ends_on,
          holidaySet,
          storedCell: stored,
          dedicatedColors,
          baseExcludeColors,
        });
        const rawTimetableText = (effective.textValue ?? '').trim();
        const timetableColorHex = (effective.colorHex ?? '').trim();
        const normalizedMemo = isAnnualShortDayTailMemo(effective.memo) ? '' : (effective.memo ?? '').trim();
        let templateLabel = rawTimetableText
          ? trackNameByTimetableTextByGrade[selectedEntry.gradeId]?.[rawTimetableText] ?? ''
          : '';
        if (!templateLabel && rawTimetableText && gradeTrackTimetableContext) {
          templateLabel = resolveTrackNameFromTimetableText(rawTimetableText, gradeTrackTimetableContext);
        }
        // 시간표명 표시는 연간시간표 입력 원문을 그대로 유지한다.
        const timetableText = rawTimetableText;
        const annualCellKey = buildAnnualCellKey(selectedEntry.gradeId, week.mondayIso, weekdayKey, slotIndex);
        const isProjectProgram = matchesAnnualProjectProgramCell(
          timetableColorHex,
          annualProjectPrograms,
          termNumber,
          effective.isBaseExcluded
        );
        const isFixedProgram = annualSlotIsFixedProgram({
          colorHex: timetableColorHex,
          regularColors,
          isProjectProgram,
        });
        const isDedicatedProgram = isDedicatedColor(timetableColorHex, dedicatedColors);
        const programKindLabel = (() => {
          if (isProjectProgram) return '프로';
          if (isFixedProgram) return '고정';
          if (isDedicatedProgram) return '전담';
          return '';
        })();
        if (
          rowFilter &&
          !rowFilter({
            annualCellKey,
            templateLabel,
            timetableText,
            timetableColorHex,
            isFixedProgram,
            isProjectProgram,
            isDedicatedProgram,
            programKindLabel,
          })
        ) {
          continue;
        }
        out.push({
          annualCellKey,
          gradeId: selectedEntry.gradeId,
          mondayIso: week.mondayIso,
          weekdayKey,
          slotIndex,
          seq: seq++,
          dateLabel: shortDateLabel(dateIso),
          weekdayLabel: weekdayLabel(weekdayKey),
          periodLabel: periodLabels[slotIndex] || `${slotIndex + 1}타임`,
          gradeLabel: gradeNumber,
          semesterLabel: String(termNumber),
          templateLabel,
          timetableText,
          timetableColorHex,
          timetableTextShrink: timetableText.length > 2,
          timetableSlotKind: effective.slotKind,
          programKindLabel,
          memo: normalizedMemo,
        });
      }
    });
  });
  return out;
}
